import React, {useCallback, useEffect, useRef} from 'react';
import styled, {createGlobalStyle} from 'styled-components';
import {useAppDispatch, useAppSelector} from 'store/customer';
import useRoomPlanner from 'components/customer/RoomPlanner/lib/useRoomPlanner';
import Preview3DErrorBoundary from 'components/customer/Preview3D/components/Preview3DErrorBoundary';
import RoomLayoutModal from 'components/customer/RoomPlanner/components/RoomLayoutModal';
import {
    setRoomLayoutModalOpen,
    getRoomLayoutModalOpen,
    getIsAutoSaving,
    getIsTopView,
    getRoomType,
    getIsEditHeightView,
    getIsEditingRoomDimension,
    getisRoomPlannerEditing,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {getIsSavingProductIn3DRP} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {useSidebarStateToggle} from 'shared/helpers/useSidebarStateToggle';
import RoomDimensionOverlay from 'components/customer/RoomPlanner/components/RoomDimensionOverlay';
import FloatingComponent from './FloatingMenu/FloatingComponent';
import FloatingMenu from './FloatingMenu/FloatingMenu';
import RoomHeightOverlay from 'components/customer/RoomPlanner/components/RoomHeightOverlay';
import {Spinner} from 'react-bootstrap';
import {Loader} from 'shared/helpers';
import {usePreCacheProducts} from 'hooks/preCacheProducts';
import {useGetLayoutsQuery} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {useParams} from 'react-router-dom';
import PositionEditor from 'components/customer/RoomPlanner/components/FloatingMenu/PositionEditor';
import {Note} from 'shared/components/Header/RoomPlanner/Note';
import AddCabinetButton3D from 'components/customer/RoomPlanner/components/AddCabinetButton3D';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import useUndoRedo from 'components/customer/RoomPlanner/lib/useUndoRedo';
import TopBar from 'components/customer/RoomPlanner/components/TopBar';
import BottomBar from 'components/customer/RoomPlanner/components/BottomBar';
import {PlaceholderDropdown} from 'components/customer/RoomPlanner/components/FloatingMenu/PlaceholderDropdown';
import {PlaceholderPreviewInfo} from 'components/customer/RoomPlanner/components/FloatingMenu/PlaceholderPreviewInfo';
import {isMobile} from 'react-device-detect';
import {ContextMenu} from 'components/customer/RoomPlanner/components/Menu/ContextMenu';
import {Mobile} from 'shared/components/Header/RoomPlanner/FloatingControls/Mobile';
import {
    mobile,
    mobileLandscape,
    mobileLandscapeAndPortraitDevices,
    mobilePortrait,
    tabletLandscape,
    tabletPortrait,
} from 'shared/Breakpoints';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
    useTabletSize,
} from 'shared/helpers/DeviceSize';
import ProductInfo from 'components/customer/Preview3D/components/ProductInfo';
import RoomStartupOverlay from 'components/customer/RoomPlanner/components/RoomStartupOverlay';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';
import {useGetRoomPreviewQuery} from 'components/customer/Preview3D/store/previewApi';
import {ProductDropdown} from 'components/customer/RoomPlanner/components/FloatingMenu/ProductDropdownFloatingComponent';
import {useRoomPlannerLeftMenu} from 'components/customer/RoomPlanner/helper/useRoomPlannerLeftMenu';
import {Beta} from 'shared/components/Beta';

const RoomPlannerGlobalStyles = createGlobalStyle`
    .content-container {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .restriction-alert-message {
        position: fixed;
        top: 62px;
        z-index: 1;
        right: 0;

        ${mobilePortrait`
            left: 5px;
            margin-left: 0;
        `}

        ${tabletPortrait`
            left: 5px;
        `}

        ${tabletLandscape`
            left: 50px;
        `}
    }

    ${mobileLandscape`
        .restriction-alert-message {
            left: 53px;
        }

        .theme-gocabinets .mainContainer .sidebar-left .dashboard-link div {
            width: 50px;
        }

        .mainContainer {
            overflow-x: hidden;
        }

        .theme-gocabinets .mainContainer .sidebar-left .dashboard-link img{
            width: 4vw;
        }

        .theme-gocabinets .mainContainer .sidebar-left .sidebar-left-content .nav-container .nav a img {
            width: 4vw;
        }
    `}

    ${tabletLandscape`
        .mainContainer {
            overflow-x: hidden;
        }
    `}

    ${mobilePortrait`
        .main-header {
            width: calc(100% - 80px);
        }
    `}

    ${mobileLandscapeAndPortraitDevices`
        .top_color_strip {
            z-index: 999999;
        }

        /* Planner TopBar is also a header; keep this on the site header only. */
        .mainContent > header {
            z-index: 99999 ;
        }
    `}
`;

const RoomPlannerDashboard = () => {
    useSidebarStateToggle();
    useUndoRedo();

    const isMobileLandscape = useIsMobileLandscape();
    const isPortraitDevices = useIsPortraitDevices();
    const isMobileDevice = isMobileLandscape || isPortraitDevices;
    const orientationKey = isMobileLandscape
        ? 'landscape'
        : isPortraitDevices
          ? 'portrait'
          : 'desktop';
    const isDesktopPlanner = !isMobile;
    // `isMobile` is the user agent, so it stays false when a desktop window is
    // resized. `useTabletSize` follows the width, same as the rest of the app.
    const isTabletSize = useTabletSize();
    const isEditingRoomDimension = useAppSelector(getIsEditingRoomDimension);
    const useTabletEditingNote = isTabletSize && isEditingRoomDimension;
    const {loading} = usePreCacheProducts();
    const canvasContainer = useRef<HTMLDivElement>();

    const {
        isGeneratingReport,
        mountRef,
        previewPositionRef,
        dialog,
        renderer,
        sideButtonGroupsRef,
        productsReady,
        isRoomSceneReady,
        showStartupOverlay,
        subscribeToProductRenderProgress,
        getRenderedProductCount,
        totalProductCount,
    } = useRoomPlanner(loading);
    const dispatch = useAppDispatch();
    const isAutoSaving = useAppSelector(getIsAutoSaving);
    const isSavingProductIn3DRP = useAppSelector(getIsSavingProductIn3DRP);
    const isRoomLayoutModalOpen = useAppSelector(getRoomLayoutModalOpen);
    const isTopView = useAppSelector(getIsTopView);
    const isEditHeightView = useAppSelector(getIsEditHeightView);
    const roomType = useAppSelector(getRoomType);
    const isRoomPreview = useRoomPreview();
    const {isMenuOpen} = useRoomPlannerLeftMenu();

    const {roomId: paramRoomId, roomSlug = ''} = useParams();
    const {data: roomPreviewData, isLoading: isLoadingRoomPreview} =
        useGetRoomPreviewQuery(
            {slug: roomSlug},
            {
                skip: !isRoomPreview || !roomSlug,
            }
        );

    const roomId = isRoomPreview
        ? String(roomPreviewData?.room.id)
        : paramRoomId;
    const {isLoading: isLayoutsLoading} = useGetLayoutsQuery(
        parseInt(String(roomId)),
        {
            skip:
                isRoomPreview ||
                !roomId ||
                roomId === 'undefined' ||
                Number.isNaN(parseInt(String(roomId))),
        }
    );
    const isRoomPlannerEditing = useAppSelector(getisRoomPlannerEditing);
    const {selectedProduct, selectedCabinetGroup, setSelectedProduct} =
        useRoomPlannerContext();
    const isPlaceholderSelected = Boolean(
        selectedCabinetGroup?.userData?.isPlaceholder
    );
    const handleModalOpen = useCallback((show: boolean) => {
        dispatch(setRoomLayoutModalOpen(show));
    }, []);

    // Mirrors the branch below that renders MobilePreviewProductPanel: the
    // panel is pinned to the bottom of the stage, so the fixed-position Note
    // would sit on top of it.
    const showMobilePreviewPanel =
        isRoomPreview &&
        (!!selectedProduct || isPlaceholderSelected) &&
        (isRoomPlannerEditing || isPortraitDevices || isMobileLandscape);

    const cabinetInfo = roomPreviewData?.product_info?.JobCabinets?.find(
        (cabinet) =>
            cabinet.Slug === selectedProduct?.id?.toString() ||
            cabinet.CabinetNumber?.toString() ===
                selectedProduct?.id?.toString()
    );

    useEffect(() => {
        if (isMobileLandscape) {
            const isLandscape = window.matchMedia(
                '(orientation: landscape)'
            ).matches;

            if (!isLandscape) return;

            if (canvasContainer.current) {
                if (selectedProduct || isPlaceholderSelected) {
                    canvasContainer.current.style.width = '50%';
                } else {
                    canvasContainer.current.style.width = 'initial';
                }
            }
        }
    }, [selectedProduct, isPlaceholderSelected, isMobileLandscape]);

    useEffect(() => {
        const cabinetId = window.location.hash.replace('#', '');
        if (productsReady && cabinetId) {
            const numericId = Number(cabinetId);
            setSelectedProduct?.(
                Number.isNaN(numericId) ? cabinetId : numericId
            );
        }
    }, [productsReady]);

    if ((!isRoomPreview && isLayoutsLoading) || isLoadingRoomPreview) {
        return <Loader loader={true} />;
    }

    return (
        <Preview3DErrorBoundary bordered={false} resetKey={orientationKey}>
            <RoomPlannerGlobalStyles />
            {dialog}
            <RoomPlannerContainer
                ref={canvasContainer}
                $isMenuOpen={isMenuOpen}
                id="room-planner-container">
                <TopBar />
                <SceneStage>
                    <SceneBeta />
                    <Loader loader={isLayoutsLoading} />
                    {isTopView && roomType ? (
                        <RoomDimensionOverlay
                            previewPositionRef={previewPositionRef}
                        />
                    ) : null}
                    {isEditHeightView && roomType ? (
                        <RoomHeightOverlay
                            previewPositionRef={previewPositionRef}
                        />
                    ) : null}
                    <FloatingComponent containerRef={mountRef}>
                        <FloatingMenu />
                    </FloatingComponent>
                    {!isRoomPlannerEditing &&
                    !isPortraitDevices &&
                    !isMobileLandscape ? (
                        isRoomPreview && selectedProduct ? (
                            <ProductInfo
                                getProductDataStore={selectedProduct}
                                getProductInfo={cabinetInfo}
                                isRoomPreview
                            />
                        ) : isRoomPreview && isPlaceholderSelected ? (
                            <PlaceholderPreviewInfo />
                        ) : (
                            <>
                                <ProductDropdown />
                                <PlaceholderDropdown />
                            </>
                        )
                    ) : isRoomPreview && selectedProduct ? (
                        <MobilePreviewProductPanel>
                            <ProductDropdown />
                        </MobilePreviewProductPanel>
                    ) : isRoomPreview && isPlaceholderSelected ? (
                        <MobilePreviewProductPanel>
                            <PlaceholderDropdown />
                        </MobilePreviewProductPanel>
                    ) : null}
                    {!!CameraSetup.scene &&
                        !!CameraSetup.camera &&
                        !!renderer.current &&
                        !isMobileDevice &&
                        !isRoomPreview && (
                            <AddCabinetButton3D
                                scene={CameraSetup.scene}
                                camera={CameraSetup.camera}
                                renderer={renderer}
                                sideButtonGroupsRef={sideButtonGroupsRef}
                            />
                        )}
                    {!isMobile ? <PositionEditor /> : null}
                    <Loader
                        loader={isGeneratingReport}
                        smallPaddingTop={true}
                        loadingText={
                            <h5 className="mx-3">
                                Please wait while we generate your report.
                            </h5>
                        }
                    />
                    <RoomPlanner ref={mountRef} />
                    {showStartupOverlay ? (
                        <RoomStartupOverlay
                            isRoomSceneReady={isRoomSceneReady}
                            totalProductCount={totalProductCount}
                            subscribeToProductRenderProgress={
                                subscribeToProductRenderProgress
                            }
                            getRenderedProductCount={getRenderedProductCount}
                        />
                    ) : null}
                    {!isLayoutsLoading && !isRoomPreview ? (
                        <RoomLayoutModal
                            open={isRoomLayoutModalOpen}
                            setModalOpen={handleModalOpen}
                        />
                    ) : null}
                    <SavingContainer
                        className={`d-flex align-items-center gap-2 transition-opacity ${
                            isAutoSaving || isSavingProductIn3DRP
                                ? 'opacity-100'
                                : 'opacity-0'
                        }`}>
                        <div className="d-flex align-items-center gap-2">
                            <Spinner animation="border" size="sm" />
                            <span>Saving</span>
                        </div>
                    </SavingContainer>
                </SceneStage>
                {isDesktopPlanner && !useTabletEditingNote ? (
                    <BottomBar />
                ) : null}
            </RoomPlannerContainer>
            {showMobilePreviewPanel ||
            (isDesktopPlanner && !useTabletEditingNote) ? null : (
                <Note />
            )}
            {isPortraitDevices ? <ContextMenu mobileView /> : null}
            {isMobile ? <Mobile /> : null}
        </Preview3DErrorBoundary>
    );
};

const RoomPlannerContainer = styled.div<{$isMenuOpen?: boolean}>`
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    container: planner-stage / inline-size;
    width: ${({$isMenuOpen}) => ($isMenuOpen ? 'calc(100vw - 235px)' : '100%')};
`;

const SceneBeta = styled(Beta)`
    z-index: 2;
    pointer-events: none;
`;

const SceneStage = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    z-index: 1;

    ${mobile`
        align-items: flex-start;
    `}
`;

const MobilePreviewProductPanel = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    background: white;
    box-shadow: 0 0 9px 1px #d1d1d1;
    padding: 12px 0 10px;
    /* Scroll inside the panel instead of being clipped by the planner
       container's overflow: hidden when the product info is tall. */
    max-height: 65%;
    overflow-y: auto;

    ${mobileLandscape`
        left: auto;
        width: 50%;
        top: 70px;
        bottom: 0;
        max-height: none;
        box-shadow: -1px -1px 8px 0px #a1a1a1 inset;
    `}
`;

const RoomPlanner = styled.div`
    height: 100%;
    flex-grow: 1;
`;

const SavingContainer = styled.div`
    position: fixed;
    top: 128px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    transition: opacity 0.5s ease-in-out;
    pointer-events: none;

    ${mobile`
        top: 85px;
    `}
`;

export default RoomPlannerDashboard;
